"use client";

import { AnimatePresence, motion } from "motion/react";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import Link from "next/link";
import { ChevronLeft, ChevronRight, Info } from "lucide-react";
import { useEffect, useRef, useState, useCallback } from "react";
import { heroImages } from "@/lib/data";

export const Hero = () => {
  const [currentImage, setCurrentImage] = useState(0);
  // const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Create a reusable function for transitioning slides
  const goToSlide = useCallback((index: number) => {
    setCurrentImage(index);
  }, []);

  // Handle next and previous navigation
  const nextSlide = useCallback(() => {
    goToSlide((currentImage + 1) % heroImages.length);
  }, [currentImage, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide(currentImage === 0 ? heroImages.length - 1 : currentImage - 1);
  }, [currentImage, goToSlide]);

  // Setup and cleanup interval with pause functionality
  useEffect(() => {
    const startInterval = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      
      intervalRef.current = setInterval(() => {
        nextSlide();
      }, 7000);
    };

    startInterval();

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [nextSlide]);

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prevSlide();
      if (e.key === "ArrowRight") nextSlide();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide]);

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prevSlide();
      if (e.key === "ArrowRight") nextSlide();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide]);

  return (
    <>
      <section id="home" className="relative">
        <div
          className="relative h-[400px] sm:h-[600px] md:h-[700px] w-full overflow-hidden"
          ref={carouselRef}
          // onMouseEnter={() => setIsPaused(true)}
          // onMouseLeave={() => setIsPaused(false)}
          role="region"
          aria-roledescription="carousel"
          aria-label="School highlights carousel"
        >
          <AnimatePresence initial={false} mode="wait">
            <motion.div
              key={currentImage}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0"
            >
              <div className="relative h-full w-full">
                <Image
                  src={heroImages[currentImage].src}
                  alt={heroImages[currentImage].alt}
                  fill
                  className="object-cover"
                  priority
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 100vw"
                  placeholder="blur"
                  blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjY2NjY2NjIi8+PC9zdmc+"
                />
                <div className="absolute inset-0 bg-black/50" />
              </div>
              
              <div className="absolute inset-0 flex items-center justify-center px-4">
                <div className="container text-center mt-20 text-white">
                  <motion.h1
                    className="mb-4 bg-gradient-to-r from-sky-400 to-pink-500 bg-clip-text text-transparent md:mb-6 text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    {heroImages[currentImage].title}
                  </motion.h1>
                  <motion.p
                    className="mb-6 md:mb-8 mx-auto max-w-2xl text-base sm:text-lg md:text-xl bg-gradient-to-l from-sky-400 to-pink-500 bg-clip-text text-transparent"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    {heroImages[currentImage].subtitle}
                  </motion.p>
                  <motion.div
                    className="flex flex-col md:flex-row gap-3 md:gap-4 justify-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    <Link href="/online-admission" className="w-full md:w-auto">
                      <Button size="lg" className="w-full md:w-auto cursor-pointer font-semibold">
                        Apply Online
                      </Button>
                    </Link>
                    <Link href="/about" className="w-full md:w-auto">
                      <Button
                        size="lg"
                        variant="outline"
                        className="w-full md:w-auto cursor-pointer bg-white/10 text-white hover:text-primary-foreground border-primary/50 hover:bg-white/20"
                      >
                        Learn More
                      </Button>
                    </Link>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Carousel Navigation Dots */}
          <div className="absolute bottom-6 left-0 right-0 flex justify-center space-x-2 z-10">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 md:w-3 md:h-3 rounded-full transition-all duration-300 ${
                  currentImage === index ? "bg-white scale-125" : "bg-white/50"
                }`}
                aria-label={`Go to slide ${index + 1}`}
                aria-current={currentImage === index ? "true" : "false"}
              />
            ))}
          </div>

          {/* Carousel Controls */}
          <button
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-1 sm:p-2 rounded-full transition-colors"
            onClick={prevSlide}
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-4 w-4 sm:h-6 sm:w-6" />
          </button>
          <button
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-1 sm:p-2 rounded-full transition-colors"
            onClick={nextSlide}
            aria-label="Next slide"
          >
            <ChevronRight className="h-4 w-4 sm:h-6 sm:w-6" />
          </button>
          
          {/* Progress indicator */}
          <div className="absolute bottom-0 left-0 w-full h-1">
            <motion.div 
              className="h-full bg-muted-foreground/90"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: 7, ease: "linear", repeat: Infinity, repeatType: "loop" }}
              style={{ 
                // display: isPaused ? "none" : "block" 
              }}
            />
          </div>
        </div>
      </section>

      {/* Announcement Banner */}
      <motion.section
        className="bg-primary text-primary-foreground py-2 md:py-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <div className="container mx-auto px-4 flex flex-col sm:flex-row items-center justify-center gap-2 text-center sm:text-left">
          <div className="flex items-center gap-1">
            <Info className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
            <p className="text-xs md:text-sm font-medium">
              <span className="font-bold">Admissions Open for 2083:</span>{" "}
              <span className="hidden sm:inline">Applications are now being accepted for all grades. Limited seats available.</span>
              <span className="inline sm:hidden">Applications open for all grades.</span>
            </p>
          </div>
          <Link
            href="/online-admission"
            className="whitespace-nowrap text-xs md:text-sm font-bold bg-accent text-primary hover:text-primary/90 px-3 py-1 rounded-full hover:bg-accent/95 hover:scale-105 transition-all"
          >
            Apply Online
          </Link>
        </div>
      </motion.section>
    </>
  );
};

export default Hero;